import React from 'react';
import { useQuery } from 'react-query';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertTriangle,
  User
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { complianceService } from '../../services/api';
import { formatDate, getStatusBadge, isOverdue, isDueSoon } from '../../utils/helpers';
import LoadingSpinner from '../Common/LoadingSpinner';
import CustomCalendar from './CustomCalendar';

const Dashboard = () => {
  const { user } = useAuth();

  // Fetch compliance statistics
  const { data: stats, isLoading: isLoadingStats } = useQuery(
    ['compliance-stats'],
    () => complianceService.getComplianceStats(),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  );

  // Fetch recent compliances
  const { data: recentCompliances, isLoading: isLoadingRecent } = useQuery(
    ['recent-compliances'],
    () => complianceService.getCompliances({ limit: 5, sort: '-createdAt' }),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  );

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const getRoleBasedStats = () => {
    if (!stats?.data?.summary) return [];

    const summary = stats.data.summary;

    const baseStats = [
      {
        name: 'Total',
        value: summary.total || 0,
        icon: FileText,
        color: 'bg-blue-500',
      },
      {
        name: 'Pending',
        value: summary.pending || 0,
        icon: Clock,
        color: 'bg-yellow-500',
      },
      {
        name: 'Approved',
        value: summary.approved || 0,
        icon: CheckCircle,
        color: 'bg-green-500',
      },
      {
        name: 'Rejected',
        value: summary.rejected || 0,
        icon: AlertTriangle,
        color: 'bg-red-500',
      },
      {
        name: 'Overdue',
        value: summary.overdue || 0,
        icon: AlertTriangle,
        color: 'bg-rose-500',
      },
      {
        name: 'Due Soon',
        value: summary.dueSoon || 0,
        icon: Clock,
        color: 'bg-orange-500',
      },
    ];

    return baseStats;
  };



  if (isLoadingStats) {
    return <LoadingSpinner text="Loading dashboard..." />;
  }

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {getGreeting()}, {user?.name}!
            </h1>
            <p className="text-gray-600 mt-1">
              Welcome to the Compliance Management System
            </p>
            <div className="flex items-center mt-2">
              <User className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-500 capitalize">
                {user?.role} • {user?.email}
              </span>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Today</p>
            <p className="text-lg font-semibold text-gray-900">
              {formatDate(new Date(), 'EEEE, MMMM dd, yyyy')}
            </p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {getRoleBasedStats().map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stat.color}`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Compliance Calendar */}
      <CustomCalendar />

      {/* Recent Compliances */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Recent Compliances</h2>
        </div>
        <div className="p-6">
          {isLoadingRecent ? (
            <LoadingSpinner text="Loading recent compliances..." />
          ) : recentCompliances?.data?.length > 0 ? (
            <div className="space-y-4">
              {recentCompliances.data.slice(0, 5).map((compliance) => (
                <div
                  key={compliance._id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <FileText className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                      <h3 className="text-sm font-medium text-gray-900">
                        {compliance.title}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Due: {formatDate(compliance.dueDate)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {getStatusBadge(compliance.status)}
                    {/* isOverdue(compliance.dueDate) && (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    ) */}
                    {isDueSoon(compliance.dueDate) && (
                      <Clock className="h-4 w-4 text-yellow-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No compliances found</p>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default Dashboard; 